/**
 * Public mobile-friendly view of a teaching guide.
 * Accessed via QR code printed on the instructor's paper guide.
 * Reuses the same data sources as TeachingGuide.js — read-only.
 * Includes a self-evaluation quiz at the bottom.
 */
import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Heart, AlertTriangle, ShieldAlert, Clock, Users, BookOpen, CheckCircle, MessageSquare, ChevronDown, ChevronUp, GraduationCap, RotateCcw } from 'lucide-react';
import { buildTeachingGuides as buildLot1 } from '@/components/health/teachingGuideDataLot1';
import { buildTeachingGuidesLot2 as buildLot2 } from '@/components/health/teachingGuideDataLot2';
import { buildTeachingGuidesLot3 as buildLot3 } from '@/components/health/teachingGuideDataLot3';
import { buildTeachingGuidesLot4 as buildLot4 } from '@/components/health/teachingGuideDataLot4';
import { buildTeachingGuidesLegacy } from '@/components/health/teachingGuideDataLegacy';
import { SELF_EVAL_QUIZZES } from '@/components/health/selfEvalQuizzes';

const ICONS = { Heart, AlertTriangle, ShieldAlert, Clock, Users };
const ALL_GUIDES = {
  ...buildLot1(ICONS),
  ...buildLot2(ICONS),
  ...buildLot3(ICONS),
  ...buildLot4(ICONS),
  ...buildTeachingGuidesLegacy(ICONS),
};

const Section = ({ title, icon: Icon, accent = '#002768', defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="mb-3 rounded-xl border border-gray-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left active:bg-gray-50"
        style={{ borderLeft: `4px solid ${accent}` }}
        data-testid={`mobile-section-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 flex-shrink-0" style={{ color: accent }} />
          <span className="text-sm font-bold text-gray-900 truncate">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-700 leading-relaxed">{children}</div>}
    </section>
  );
};

const SelfEval = ({ quiz }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const score = useMemo(() => {
    if (!submitted) return 0;
    return quiz.questions.reduce((acc, q, i) => acc + (answers[i] === q.correct ? 1 : 0), 0);
  }, [submitted, answers, quiz.questions]);

  const reset = () => { setAnswers({}); setSubmitted(false); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  if (submitted) {
    const pct = Math.round((score / quiz.questions.length) * 100);
    const passed = pct >= 70;
    return (
      <div className="mt-4 p-5 rounded-2xl border-2 text-center" style={{ borderColor: passed ? '#16a34a' : '#dc2626', backgroundColor: passed ? '#f0fdf4' : '#fef2f2' }} data-testid="self-eval-result">
        <GraduationCap className="w-10 h-10 mx-auto mb-2" style={{ color: passed ? '#16a34a' : '#dc2626' }} />
        <div className="text-3xl font-bold mb-1" style={{ color: passed ? '#16a34a' : '#dc2626' }}>{score} / {quiz.questions.length}</div>
        <div className="text-sm text-gray-600 mb-3">{pct}% — {passed ? 'Bravo, vous maîtrisez les bases !' : 'Reprenez le guide et réessayez.'}</div>
        <div className="text-left space-y-3 mt-4">
          {quiz.questions.map((q, i) => {
            const ok = answers[i] === q.correct;
            return (
              <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: ok ? '#dcfce7' : '#fee2e2' }}>
                <div className="text-xs font-bold mb-1" style={{ color: ok ? '#15803d' : '#991b1b' }}>
                  {ok ? '✓' : '✗'} Question {i + 1}
                </div>
                <div className="text-sm text-gray-800 mb-2">{q.q}</div>
                {!ok && (
                  <div className="text-xs text-gray-700">
                    <strong>Bonne réponse :</strong> {q.options[q.correct]}
                  </div>
                )}
                {q.explanation && (
                  <div className="text-xs text-gray-600 italic mt-1">💡 {q.explanation}</div>
                )}
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={reset}
          className="mt-5 flex items-center gap-2 mx-auto px-5 py-2.5 rounded-full text-white text-sm font-semibold"
          style={{ backgroundColor: '#002768' }}
          data-testid="self-eval-restart"
        >
          <RotateCcw className="w-4 h-4" /> Recommencer
        </button>
      </div>
    );
  }

  const answered = Object.keys(answers).length;
  return (
    <div className="mt-4" data-testid="self-eval-quiz">
      <div className="space-y-4">
        {quiz.questions.map((q, i) => (
          <div key={i} className="p-4 rounded-xl border border-gray-200 bg-white">
            <div className="text-xs font-bold text-gray-400 mb-1">Question {i + 1} / {quiz.questions.length}</div>
            <div className="text-sm font-semibold text-gray-900 mb-3">{q.q}</div>
            <div className="space-y-2">
              {q.options.map((opt, j) => {
                const selected = answers[i] === j;
                return (
                  <button
                    key={j}
                    type="button"
                    onClick={() => setAnswers({ ...answers, [i]: j })}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm border-2 transition ${selected ? 'border-blue-600 bg-blue-50 text-blue-900 font-medium' : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'}`}
                    data-testid={`self-eval-q${i}-opt${j}`}
                  >
                    <span className="inline-block w-5 h-5 mr-2 rounded-full border-2 align-middle" style={{ borderColor: selected ? '#2563eb' : '#d1d5db', backgroundColor: selected ? '#2563eb' : 'transparent' }} />
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        disabled={answered < quiz.questions.length}
        onClick={() => { setSubmitted(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        className="mt-5 w-full py-3 rounded-full text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ backgroundColor: '#002768' }}
        data-testid="self-eval-submit"
      >
        Voir mon score ({answered}/{quiz.questions.length})
      </button>
    </div>
  );
};

const MobileGuide = () => {
  const { protocolId } = useParams();
  const guide = ALL_GUIDES[protocolId];
  const quiz = SELF_EVAL_QUIZZES[protocolId];
  const [tab, setTab] = useState('guide'); // guide | eval

  if (!guide) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
        <div className="text-center" data-testid="mobile-guide-not-found">
          <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h1 className="text-lg font-bold text-gray-800 mb-1">Guide indisponible</h1>
          <p className="text-sm text-gray-500">Le protocole « {protocolId} » n&apos;est pas reconnu.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-12" data-testid="mobile-guide-page">
      {/* Header */}
      <header className="px-5 pt-6 pb-4 text-white" style={{ background: 'linear-gradient(135deg, #002768 0%, #1e40af 100%)' }}>
        <p className="text-[10px] uppercase tracking-widest font-bold opacity-80 mb-1">Simulateur 911 — Collège Montmorency</p>
        <h1 className="text-2xl font-bold leading-tight">{guide.title}</h1>
        <p className="text-sm opacity-90 mt-1">{guide.subtitle}</p>
      </header>

      {/* Tabs */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-3 flex gap-1 shadow-sm">
        <button
          type="button"
          onClick={() => setTab('guide')}
          className={`flex-1 py-3 text-sm font-semibold ${tab === 'guide' ? 'text-blue-700 border-b-2 border-blue-600' : 'text-gray-500'}`}
          data-testid="mobile-tab-guide"
        >
          📖 Guide
        </button>
        <button
          type="button"
          onClick={() => setTab('eval')}
          className={`flex-1 py-3 text-sm font-semibold ${tab === 'eval' ? 'text-blue-700 border-b-2 border-blue-600' : 'text-gray-500'}`}
          data-testid="mobile-tab-eval"
        >
          ✍️ Auto-évaluation
          {quiz && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{quiz.questions.length}</span>}
        </button>
      </div>

      <main className="px-3 pt-4">
        {tab === 'guide' && (
          <>
            {/* Hero — Essentiel */}
            <div className="mb-4 p-4 rounded-2xl text-white shadow-md" style={{ background: 'linear-gradient(135deg, #002768 0%, #2563eb 100%)' }} data-testid="mobile-essentiel">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">🎯</span>
                <h2 className="font-bold text-base">Ce qu&apos;il faut retenir</h2>
              </div>
              <ol className="list-decimal pl-5 space-y-1.5 text-sm leading-snug">
                {guide.objectives.slice(0, 4).map((o, i) => <li key={i}>{o}</li>)}
              </ol>
            </div>

            {/* Drapeaux rouges — toujours visibles, scannable */}
            <div className="mb-4 p-4 rounded-2xl border-2 border-red-200 bg-red-50" data-testid="mobile-redflags">
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="w-5 h-5 text-red-700" />
                <h2 className="font-bold text-base text-red-900">Drapeaux rouges</h2>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {guide.red_flags.map((rf, i) => (
                  <span key={i} className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-white text-red-700 border border-red-300">
                    🚩 {rf}
                  </span>
                ))}
              </div>
            </div>

            {/* Phrases golden */}
            <div className="mb-4 grid grid-cols-1 gap-3" data-testid="mobile-phrases">
              <div className="p-4 rounded-2xl border-2 border-green-200 bg-green-50">
                <h3 className="font-bold text-sm text-green-900 mb-2">✅ À dire</h3>
                <div className="space-y-1.5">
                  {guide.citizen_behavior.phrases_utiles.slice(0, 3).map((p, i) => (
                    <div key={i} className="p-2 bg-white rounded-lg text-xs italic border-l-2 border-green-400">{p}</div>
                  ))}
                </div>
              </div>
              <div className="p-4 rounded-2xl border-2 border-orange-200 bg-orange-50">
                <h3 className="font-bold text-sm text-orange-900 mb-2">❌ À éviter</h3>
                <div className="space-y-1.5">
                  {guide.citizen_behavior.phrases_a_eviter.slice(0, 3).map((p, i) => (
                    <div key={i} className="p-2 bg-white rounded-lg text-xs italic border-l-2 border-orange-400">{p}</div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pré-arrivée */}
            <div className="mb-4 p-4 rounded-2xl border-2 border-amber-200 bg-amber-50" data-testid="mobile-prearrival">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-700" />
                <h2 className="font-bold text-base text-amber-900">Consignes pré-arrivée</h2>
              </div>
              <ol className="list-decimal pl-5 space-y-1 text-sm">
                {guide.pre_arrival.slice(0, 6).map((p, i) => <li key={i}>{p}</li>)}
              </ol>
            </div>

            {/* CTA Auto-éval */}
            {quiz && (
              <button
                type="button"
                onClick={() => { setTab('eval'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="w-full mb-4 py-4 rounded-2xl text-white text-sm font-bold shadow-lg active:scale-[0.98] transition flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #002768 0%, #2563eb 100%)' }}
                data-testid="mobile-cta-eval"
              >
                <GraduationCap className="w-5 h-5" />
                Tester mes connaissances ({quiz.questions.length} questions)
              </button>
            )}

            {/* Détails complets — accordion */}
            <div className="mt-6 mb-2">
              <p className="text-xs text-gray-400 uppercase tracking-widest font-bold text-center mb-2">— Aller plus loin —</p>
            </div>

            <Section title="Contexte et statistiques" icon={BookOpen}>
              <p className="mb-3 whitespace-pre-line">{guide.intro}</p>
              <div className="space-y-2 mt-3">
                {guide.stats.map((s, i) => {
                  const I = s.icon;
                  return (
                    <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-blue-50">
                      <I className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-700" />
                      <span className="text-xs">{s.text}</span>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="Phases du protocole en détail" icon={Clock} accent="#2563eb">
              {guide.phases.map(p => (
                <div key={p.n} className="mb-4 p-3 rounded-lg bg-gray-50 border-l-4 border-blue-500">
                  <h3 className="font-bold text-sm text-gray-900 mb-1">
                    <span className="inline-block w-6 h-6 rounded-full bg-blue-600 text-white text-[11px] font-bold leading-6 text-center mr-2">{p.n}</span>
                    {p.title}
                  </h3>
                  <p className="text-xs text-gray-600 italic mb-2"><strong>Pourquoi :</strong> {p.why}</p>
                  <p className="text-xs font-semibold text-gray-700">Comment :</p>
                  <ul className="list-disc pl-5 text-xs space-y-0.5 mb-2">
                    {p.how.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                  <p className="text-xs font-semibold text-red-700">⚠ Pièges :</p>
                  <ul className="list-disc pl-5 text-xs space-y-0.5 text-red-700">
                    {p.pitfalls.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              ))}
            </Section>

            <Section title="Posture & gestion de la panique" icon={Users} accent="#c2410c">
              <h3 className="font-semibold mb-1.5 text-xs">🎤 Ton vocal</h3>
              <ul className="list-disc pl-5 space-y-0.5 text-xs mb-3">
                {guide.citizen_behavior.tone.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
              <h3 className="font-semibold mb-1.5 text-xs">🧘 Gestion panique</h3>
              <ul className="list-disc pl-5 space-y-0.5 text-xs">
                {guide.citizen_behavior.panique_gestion.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </Section>

            <Section title="Erreurs courantes" icon={AlertTriangle} accent="#dc2626">
              <ul className="list-disc pl-5 space-y-1 text-xs">
                {guide.common_errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </Section>

            <Section title="Références" icon={BookOpen}>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                {guide.references.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </Section>
          </>
        )}

        {tab === 'eval' && (
          quiz ? (
            <>
              <div className="mb-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
                <div className="flex items-center gap-2 mb-1">
                  <GraduationCap className="w-5 h-5 text-blue-700" />
                  <h2 className="font-bold text-blue-900">{quiz.title}</h2>
                </div>
                <p className="text-xs text-blue-800">{quiz.description}</p>
              </div>
              <SelfEval quiz={quiz} />
            </>
          ) : (
            <div className="text-center py-12 text-gray-400 text-sm" data-testid="mobile-no-quiz">
              <MessageSquare className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              Auto-évaluation à venir pour ce protocole.
            </div>
          )
        )}

        <footer className="text-center mt-8 pt-4 border-t border-gray-200 text-[10px] text-gray-400">
          Simulateur 911 — Collège Montmorency<br />
          Document pédagogique • Auto-évaluation formative
        </footer>
      </main>
    </div>
  );
};

export default MobileGuide;
